import React, { useState, useEffect } from "react";
import { AssessmentHeader } from "../components/ui/assessments/assessment-header";
import { AssessmentProgress } from "../components/ui/assessments/assessment-progress";
import { AssessmentQuestion } from "../components/ui/assessments/assessment-question";
import { AssessmentOption } from "../components/ui/assessments/assessment-option";
import { AssessmentNavigation } from "../components/ui/assessments/assessment-navigation";
import { CareerCard } from "../components/ui/assessments/assessment-career-card";
import { AssessmentResults } from "../components/ui/assessments/assessment-results";
import { CareerModal } from "../components/ui/career-modal";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Code, Palette, Zap, Target, ArrowRight } from "lucide-react";
import ProtectedRoute from "../components/ProtectedRoute";
import { useNavigate } from "react-router"; // Add this import

// Define the Career type
const CareerAssessmentPage = () => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(1);
  const [answers, setAnswers] = useState({});
  const [selectedOption, setSelectedOption] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate(); // Add this hook

  const totalQuestions = 15;

  const careerData = {
    personalityType: "Creative Innovator",
    strengths: ["Problem Solving", "Creativity", "Teamwork", "Adaptability"],
    topCareers: [
      {
        title: "Game Designer",
        description: "Create fun games and interactive experiences",
        salary: "$75,000 - $120,000",
        education: "Bachelor's in Game Design",
        funFact: "You get to play games for research!",
        growth: "Fast Growing",
        match: 95,
        icon: <Zap className="w-6 h-6 text-indigo-600" />,
      },
      {
        title: "App Developer",
        description: "Build cool apps that help people",
        salary: "$80,000 - $130,000",
        education: "Bachelor's in Computer Science",
        funFact: "You can create the next viral app!",
        growth: "Very High",
        match: 88,
        icon: <Code className="w-6 h-6 text-indigo-600" />,
      },
      {
        title: "Digital Artist",
        description: "Design amazing graphics and animations",
        salary: "$60,000 - $100,000",
        education: "Bachelor's in Digital Arts",
        funFact: "Your art could be in movies or games!",
        growth: "Growing Fast",
        match: 82,
        icon: <Palette className="w-6 h-6 text-indigo-600" />,
      },
      {
        title: "UX Designer",
        description: "Make apps and websites easy to use",
        salary: "$70,000 - $110,000",
        education: "Bachelor's in Design",
        funFact: "You help people love technology!",
        growth: "High Demand",
        match: 78,
        icon: <Target className="w-6 h-6 text-indigo-600" />,
      },
    ],
    matchScore: 88,
  };

  const questions = [
    {
      id: 1,
      text: "What type of work environment do you prefer?",
      options: [
        { value: "structured", label: "Structured and organized" },
        { value: "creative", label: "Creative and flexible" },
        { value: "collaborative", label: "Team-oriented and social" },
        { value: "independent", label: "Independent and self-directed" },
      ],
    },
    {
      id: 2,
      text: "How do you prefer to solve problems?",
      options: [
        { value: "analytical", label: "Analyze data and patterns" },
        { value: "creative", label: "Brainstorm creative solutions" },
        { value: "practical", label: "Use hands-on approaches" },
        { value: "collaborative", label: "Discuss with others" },
      ],
    },
    {
      id: 3,
      text: "What subjects interest you the most?",
      options: [
        { value: "stem", label: "Science and Technology" },
        { value: "arts", label: "Arts and Humanities" },
        { value: "business", label: "Business and Finance" },
        { value: "social", label: "Social Sciences" },
      ],
    },
    {
      id: 4,
      text: "How do you handle deadlines?",
      options: [
        { value: "plan_ahead", label: "Plan and finish early" },
        { value: "thrive_pressure", label: "Work well under pressure" },
        { value: "consistent", label: "Steady and consistent pace" },
        { value: "flexible", label: "Adapt as needed" },
      ],
    },
    {
      id: 5,
      text: "What type of impact do you want to make?",
      options: [
        { value: "social", label: "Help people directly" },
        { value: "technical", label: "Create innovative products" },
        { value: "environmental", label: "Protect the environment" },
        { value: "educational", label: "Teach and inspire others" },
      ],
    },
    {
      id: 6,
      text: "How do you prefer to learn new things?",
      options: [
        { value: "reading", label: "Reading and research" },
        { value: "hands_on", label: "Hands-on practice" },
        { value: "visual", label: "Visual demonstrations" },
        { value: "discussion", label: "Group discussions" },
      ],
    },
    {
      id: 7,
      text: "What motivates you at work?",
      options: [
        { value: "achievement", label: "Achieving goals" },
        { value: "recognition", label: "Recognition from others" },
        { value: "learning", label: "Learning new skills" },
        { value: "balance", label: "Work-life balance" },
      ],
    },
    {
      id: 8,
      text: "How do you handle feedback?",
      options: [
        { value: "welcome", label: "Welcome and seek it out" },
        { value: "reflect", label: "Reflect and improve" },
        { value: "selective", label: "Consider it carefully" },
        { value: "prefer_private", label: "Prefer private feedback" },
      ],
    },
    {
      id: 9,
      text: "What's your communication style?",
      options: [
        { value: "direct", label: "Direct and straightforward" },
        { value: "diplomatic", label: "Diplomatic and tactful" },
        { value: "enthusiastic", label: "Enthusiastic and energetic" },
        { value: "reserved", label: "Reserved and thoughtful" },
      ],
    },
    {
      id: 10,
      text: "How important is work-life balance?",
      options: [
        { value: "very_important", label: "Extremely important" },
        { value: "important", label: "Very important" },
        { value: "moderate", label: "Moderately important" },
        { value: "flexible", label: "Flexible about it" },
      ],
    },
    {
      id: 11,
      text: "What type of challenges do you enjoy?",
      options: [
        { value: "technical", label: "Technical problems" },
        { value: "creative", label: "Creative challenges" },
        { value: "strategic", label: "Strategic planning" },
        { value: "interpersonal", label: "People-related challenges" },
      ],
    },
    {
      id: 12,
      text: "How do you make decisions?",
      options: [
        { value: "logical", label: "Based on logic and data" },
        { value: "intuitive", label: "Follow intuition" },
        { value: "collaborative", label: "Consult with others" },
        { value: "practical", label: "Consider practical outcomes" },
      ],
    },
    {
      id: 13,
      text: "What role do you typically play in teams?",
      options: [
        { value: "leader", label: "Leader and organizer" },
        { value: "specialist", label: "Specialist and expert" },
        { value: "facilitator", label: "Facilitator and supporter" },
        { value: "innovator", label: "Innovator and idea generator" },
      ],
    },
    {
      id: 14,
      text: "How important is career growth to you?",
      options: [
        { value: "very_important", label: "Extremely important" },
        { value: "important", label: "Very important" },
        { value: "moderate", label: "Moderately important" },
        { value: "stable", label: "Prefer stability over growth" },
      ],
    },
    {
      id: 15,
      text: "What kind of legacy do you want to leave?",
      options: [
        { value: "impact", label: "Make a positive impact" },
        { value: "innovation", label: "Create something new" },
        { value: "knowledge", label: "Share knowledge with others" },
        { value: "relationships", label: "Build strong relationships" },
      ],
    },
  ];

  useEffect(() => {
    setSelectedOption(answers[`question-${currentQuestionIndex}`] || "");
  }, [currentQuestionIndex, answers]);

  const handleOptionSelect = (value) => {
    setSelectedOption(value);
    setAnswers((prev) => ({
      ...prev,
      [`question-${currentQuestionIndex}`]: value,
    }));
  };

  const handleCareerCardClick = (career) => {
    setSelectedCareer(career);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCareer(null);
  };

  const animateQuestionChange = (direction) => {
    if (isAnimating) return;

    setIsAnimating(true);

    setTimeout(() => {
      if (direction === "next") {
        if (currentQuestionIndex < totalQuestions) {
          setCurrentQuestionIndex((prev) => prev + 1);
        } else {
          setCurrentQuestionIndex(totalQuestions + 1);
          setCelebrate(true);
          setTimeout(() => {
            setCelebrate(false);
            setShowResults(true);
          }, 2000);
        }
      } else {
        if (currentQuestionIndex > 1) {
          setCurrentQuestionIndex((prev) => prev - 1);
        }
      }

      setTimeout(() => setIsAnimating(false), 300);
    }, 300);
  };

  const nextQuestion = () => animateQuestionChange("next");
  const previousQuestion = () => animateQuestionChange("prev");

  const isCompleted = currentQuestionIndex > totalQuestions;
  const hasAnswer = !!answers[`question-${currentQuestionIndex}`];
  const currentQuestion = questions[currentQuestionIndex - 1];

  const resetQuiz = () => {
    setCurrentQuestionIndex(1);
    setAnswers({});
    setSelectedOption("");
    setShowResults(false);
    setCelebrate(false);
    setSelectedCareer(null);
    setIsModalOpen(false);
  };

  const goToCareerPath = () => {
    navigate("/career-path");
  };

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-white">
        <div className="flex-1 overflow-auto p-4 md:p-8">
          <div className="max-w-6xl mx-auto mb-8">
            <AssessmentHeader showResults={showResults} />
          </div>

          <div className="max-w-6xl mx-auto">
            {!showResults && (
              <AssessmentProgress
                currentQuestion={currentQuestionIndex}
                totalQuestions={totalQuestions}
              />
            )}

            {!showResults && (
              <Card className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200">
                {!isCompleted && currentQuestion && (
                  <div className="text-center">
                    <AssessmentQuestion
                      text={currentQuestion.text}
                      isAnimating={isAnimating}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                      {currentQuestion.options.map((option) => (
                        <AssessmentOption
                          key={option.value}
                          label={option.label}
                          value={option.value}
                          isSelected={selectedOption === option.value}
                          isAnimating={isAnimating}
                          onClick={handleOptionSelect}
                        />
                      ))}
                    </div>

                    <AssessmentNavigation
                      onPrevious={previousQuestion}
                      onNext={nextQuestion}
                      canGoPrevious={currentQuestionIndex > 1}
                      canGoNext={hasAnswer}
                      isAnimating={isAnimating}
                      isLastQuestion={currentQuestionIndex === totalQuestions}
                      currentQuestion={currentQuestionIndex}
                      totalQuestions={totalQuestions}
                    />
                  </div>
                )}

                {isCompleted && (
                  <AssessmentResults
                    onGoToCareerExplorer={resetQuiz}
                    onRetakeQuiz={resetQuiz}
                  />
                )}
              </Card>
            )}

            {showResults && (
              <Card className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200">
                <div className="text-center mb-8">
                  <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4 border border-teal-200">
                    Your Perfect Career Matches
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                    Based on Your Personality
                  </h2>
                  <p className="text-slate-600 max-w-2xl mx-auto">
                    We found {careerData.topCareers.length} careers that match
                    your interests and strengths!
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {careerData.topCareers.map((career, index) => (
                    <CareerCard
                      key={index}
                      title={career.title}
                      description={career.description}
                      salary={career.salary}
                      education={career.education}
                      funFact={career.funFact}
                      match={career.match}
                      icon={career.icon}
                      onClick={() => handleCareerCardClick(career)}
                    />
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                  <Button
                    onClick={resetQuiz}
                    className="px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-lg flex-1 max-w-xs transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  >
                    Retake Quiz
                  </Button>
                  <Button
                    onClick={goToCareerPath}
                    className="px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white rounded-xl font-bold text-lg flex-1 max-w-xs transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                  >
                    Go to Career Path
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
              </Card>
            )}

            {/* Career Modal */}
            <CareerModal
              career={selectedCareer}
              isOpen={isModalOpen}
              onClose={handleCloseModal}
            />

            {!showResults && !isCompleted && (
              <div className="text-center mt-6">
                <div className="inline-flex items-center space-x-2 bg-indigo-50 text-indigo-700 px-4 py-2 rounded-full text-sm border border-indigo-200">
                  <span>Choose what feels most authentic to you!</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
};

export default CareerAssessmentPage;
